import type { ReactNode } from 'react';
import './Avatar.css';

type AvatarSize = 'sm' | 'md' | 'lg';

export type AvatarProps = {
    /** Full name — used for initials fallback and aria-label */
    name: string;
    /** Optional image URL; if provided, renders <img> instead of initials */
    src?: string;
    size?: AvatarSize;
    online?: boolean;
    /** Overrides initials (e.g. a lucide icon for group avatars) */
    icon?: ReactNode;
    /** Optional className passthrough */
    className?: string;
};

function getInitials(name: string): string {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    const letters = parts
        .slice(0, 2)
        .map((w) => w[0])
        .filter(Boolean)
        .join('');
    return letters.toUpperCase() || '?';
}

function Avatar({ name, src, size = 'md', online = false, icon, className = '' }: AvatarProps) {
    const classes = [
        'ui-avatar',
        `ui-avatar--${size}`,
        online ? 'ui-avatar--online' : '',
        className,
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <div className={classes} aria-label={name}>
            {src ? (
                <img src={src} alt={name} className="ui-avatar__image" />
            ) : icon ? (
                <span className="ui-avatar__icon">{icon}</span>
            ) : (
                <span className="ui-avatar__initials">{getInitials(name)}</span>
            )}
            {online && <span className="ui-avatar__status" aria-hidden="true" />}
        </div>
    );
}

export default Avatar;
